import { AddressLink, PtoOnChainCreateRentalPoolCall } from 'Modules'
import { decodeFunctionData, parseEventLogs } from 'viem'

import abiRevenueSharingRentalFactory from 'abi/abi-revenue-sharing-rental-factory.json'
import { getContract } from 'hooks/contract/use-contract-viem'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError, logInfo } from 'utils/logger'

/**
 * RevenueSharingRentalFactoryV2 - the BE prepares the createRevenueRentalPool
 * calldata (POST /admin/pto/:id/revenue-sharing-rental/v2); the admin wallet
 * decodes it, simulates and sends the tx to the factory address returned by
 * the BE.
 */
export const useDeployRentalRevenueSharingContract = () => {
  const deployRentalPool = async (address: AddressLink, call: PtoOnChainCreateRentalPoolCall) => {
    try {
      if (!address) throw new Error('Wallet not connected')
      if (!call?.contractAddress) throw new Error('Rental factory address is missing')
      if (!call?.calldata) throw new Error('Create rental pool calldata is missing')

      // decode instead of sending raw bytes so the tx goes through the same
      // simulate -> write flow as the other admin on-chain calls
      const { functionName, args } = decodeFunctionData({
        abi: abiRevenueSharingRentalFactory as any,
        data: call.calldata as `0x${string}`,
      })

      const contract = getContract({
        contractAddress: call.contractAddress as `0x${string}`,
        ABI: abiRevenueSharingRentalFactory,
      })

      logInfo('Deploy Rental Pool params', call.params)

      const { request } = await contract.simulate[functionName](args, {
        account: address,
      })
      logInfo('Simulate success, writing contract...')

      if (!clientWallet) throw new Error('Wallet client not available')

      const txHash = await clientWallet.writeContract(request)
      logInfo('Transaction hash', txHash)

      const receipt = await publicWallet.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 5,
      })
      logInfo('Transaction receipt status', receipt.status)

      const logs: any = parseEventLogs({
        abi: abiRevenueSharingRentalFactory,
        logs: receipt.logs,
        eventName: 'RentalPoolCreated',
      })

      if (logs.length > 0) {
        const poolAddress = logs[0].args.poolAddress
        logInfo('Deployed rental pool address', poolAddress)
        return poolAddress as string
      }

      return null
    } catch (error) {
      logError('error deploy rental revenue sharing contract', error)
      throw error
    }
  }

  return {
    deployRentalPool,
  }
}
